import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "../../Components/UI/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { Plus, X, Check, ArrowLeft, Trash2, Settings } from "lucide-react";
import { GiIceCube } from "react-icons/gi";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { getUsersArr, getAgencyDetails, checkIfInqOnlyMode, isSmartSellerMode, selectConnFieldPref, selectThreadFieldPref } from "../../Store/Reducers";
import { format } from 'date-fns';
import { countries, cityToStateMap } from "../../Components/Enums/DataEnums";
import ConnectionFieldsDialog from "./ConnectionFields";
import { manageConnectionAPI } from './ConnectionAPI';
import { manageThreadAPI } from '../Threads/ThreadsAPI'; // Import Thread API
import apiService from '../../Services/APIService';
import { getAuthData } from '../../Utils/AuthUtils';
import { useTax } from "../../Hooks/useTax";
import { useCurrency } from "../../Hooks/useCurrency";
import parsePhoneNumberFromString from "libphonenumber-js";
import { ContactList } from "../../Components/Common/ContactList";
import ConnectionFormFields from "./ConnectionFormFields";
import { AlertBox } from "../../Components/Common/AlertBox";
import {
    getTodayDate, validateTaskDateTime, getNextHourTime, getTomorrowDate, isContactPickerSupported, isMobileDevice, getRoundedUpTime,
    getConnectionByID, getMobileHeaderTitle, currentUserHasPermission, isIOSDevice
} from "../../Utils/Utilities";
import { compressImage } from "../../Utils/ImageCompressor";
import MastersDialog from "../Settings/Masters";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../Hooks/useIsMobile";
import ConnectionDetailsWrapper from './ConnectionDetailsWrapper';
import { useActiveThreadsByConnID, useTasksByConnID } from "../../Hooks/useConnectionData";
import { getFromIndexedDB } from "../../Utils/IndexedDBUtils";
import { getAgencyApiOptions } from "../../Utils/Utilities";

// Reusable Tailwind classes
const connClasses = {
    buttonPrimary: "sahi-primary-button",
    buttonSecondary: "sahi-secondary-button",
    iconButton: "h-9 w-9 p-2 hover:bg-gray-200 sahi-radius",
    dialogResponsive: "w-[100vw] max-w-[1200px] min-h-0 max-h-[100vh] lg:max-w-[1000px] xl:max-w-[1000px]",
    dialogNoOpenSection: "w-[100vw] max-w-[1200px] min-h-0 max-h-[100vh] lg:max-w-[870px] xl:max-w-[890px]",
    dialogSmall: "w-[95vw] max-w-md sm:max-w-lg",
    dialogMobile: "w-[90vw] max-w-md",
    gridBasic: "grid grid-cols-4 gap-4",
    gridResponsive: "grid grid-cols-1 sm:grid-cols-4 gap-4",
    gridFullWidth: "grid grid-cols-1 sm:grid-cols-12 gap-4",
    flexCenter: "flex items-center justify-center",
    flexBetween: "flex items-center justify-between",
    spaceY: "space-y-1.5",
    padding: "px-4 sm:px-4",
    paddingSmall: "p-2 sm:p-4"
};

// Main dialog component for adding/editing connections
const ConnectionEntryDialog = ({
    triggerButton = true,
    triggerText = "Add Contact",
    triggerClassName = "bg-sahi-primary-color text-white sahi-primary-button",
    open: externalOpen,
    onOpenChange: externalOnOpenChange,
    connID = null,
    onConnectionAdded,
    onConnectionSaved,
    parentDialogSetter,
    initialConnType = "all",
    prefilledData = null,
    calledFrom = '', // THR: Threads, TXN: Transactions, REC: Records
    scrollToEnd = false,
    forceOpen = false,
    threadTypeID = 1,
    isBasic = false,
    editMode = 'ALL', // 'ALL', 'CONNECTION_ONLY', 'CONTACTS_ONLY'
    selectedContactID = null,
    showEnterInquiry = true
}) => {
    const dispatch = useDispatch();
    const authData = getAuthData();
    const currencySymbol = useCurrency();
    const canDltConnections = currentUserHasPermission('ConnDel');
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const isEditingContact = connID !== null && connID !== 0;
    const canSeeMoreDetails = currentUserHasPermission('AgnConfig');

    const handleOpenChange = (open) => {
        if (!open && forceOpen) {
            return;
        }
        externalOnOpenChange?.(open);
    };

    const [dialogStates, setDialogStates] = useState({
        main: false,
        fields: false,
        deleteAlert: false,
        masterDialog: false,
        coldAlert: false,
    });
    const { data: connectionData } = useSelector(state => state.connectionData);
    const threadFieldsPrefs = useSelector(state => state.threadFieldsPrefs);

    // Inquiry State - Auto-check if forced OR if no connections exist (first time user)
    const [isInquiryChecked, setIsInquiryChecked] = useState(
        !isBasic && (calledFrom === 'FORCE_INQUIRY' || (calledFrom === 'THREAD_ENTRY' && isMobile) || (calledFrom !== 'THR' && connectionData?.length === 0 && isMobile)) && threadTypeID !== 2
    );
    const [isMoreDetailsChecked, setIsMoreDetailsChecked] = useState(false);
    const [inquiryData, setInquiryData] = useState({
        product: '',
        model: '',
        quantity: '',
        businessValue: '',
        dueDT: getTomorrowDate(),
        notes: '',
        reference: '',
        stageID: null  // New field for inquiry stage/status
    });

    const [formData, setFormData] = useState({
        connectionCategory: "Business",
        recordType: "",
        recordDate: getTodayDate(),
        recordTime: getRoundedUpTime(),
        interactionBy: "",
        recordNotes: "",
        nextTaskDT: getTomorrowDate(),
        pocUserID: authData?.UserID ? [authData.UserID.toString()] : [],
        nextTaskPocUserID: "",
        attachment: '',
        attachmentName: "",
    });
    const isOpen = externalOpen !== undefined ? externalOpen : dialogStates.main;
    const setIsOpen = externalOnOpenChange || ((open) => setDialogStates(prev => ({ ...prev, main: open })));

    // Get active threads for checking if connection has open inquiries
    const activeThreads = useActiveThreadsByConnID(connID);
    const hasOpenInquiries = activeThreads && activeThreads.length > 0;

    // Get active/pending tasks for checking if connection can be deleted
    const tasksForConn = useTasksByConnID(connID);
    const hasActiveTasks = tasksForConn && tasksForConn.length > 0;

    // Reset inquiry state when dialog opens/closes or mode changes
    useEffect(() => {
        if (isOpen && !activeConnID) {
            setIsInquiryChecked(
                !isBasic && (calledFrom === 'FORCE_INQUIRY' || (calledFrom === 'THREAD_ENTRY' && isMobile) || (calledFrom !== 'THR' && connectionData?.length === 0)) && threadTypeID !== 2
            );
            setIsMoreDetailsChecked(false);
            setInquiryData({
                product: '',
                businessValue: '',
                dueDT: getTomorrowDate(),
                notes: '',
                reference: '',
                stageID: null
            });
        }
    }, [isOpen, calledFrom, connID]);
    const [masterDialogType, setMasterDialogType] = useState(null);
    const [isSteptwo, setIsSteptwo] = useState(false);
    const taxIDLabel = useTax(formData.country);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [savedConnID, setSavedConnID] = useState(null);
    const [internalConnID, setInternalConnID] = useState(connID);
    const [savedThreadID, setSavedThreadID] = useState(null); // Store Thread ID for Step 2

    // Update internal ID if prop changes
    useEffect(() => {
        setInternalConnID(connID);
    }, [connID]);

    const activeConnID = internalConnID;

    const masterData = {
        cities: useSelector((state) => state.masterData.cities || []),
        sources: useSelector((state) => state.masterData.sources || []),
        segments: useSelector((state) => state.masterData.segments || []),
        recordTypes: useSelector((state) => state.masterData.recordTypes || []), // Only record types now
        extraFields: useSelector((state) => state.masterData.connExtraFields || []),
        users: useSelector(getUsersArr),
        agencyData: useSelector(getAgencyDetails),
        regions: useSelector((state) => state.masterData.regions),
        products: useSelector((state) => state.masterData.products || []),
        thrExtraFields: useSelector(state => state.masterData.thrExtraFields || [])
    };

    const getCountryCode = () => {
        const agencyCountry = masterData.agencyData?.Country || "India";
        const countryObj = countries.find(c => c.Country === agencyCountry);
        return countryObj ? countryObj.CC : "91";
    };

    const [uiStates, setUIStates] = useState({
        selectedFields: ["name", "businessName", "mobile", "email"],
        datePickerStates: {},
        selectedCountryCode: getCountryCode(),
        selectedField: "",
        isInitialLoad: true,
        isLoadingConnection: false,
        fetchedConnectionData: null,
        showMoreDetails: false, // Always start with false
        masterSearchText: '',
    });
    const [selectedRegion, setSelectedRegion] = useState("")

    const [contactsData, setContactsData] = useState([
        { name: "", mobile: "", email: "", designation: "", countryCode: "91", isPrimary: true }
    ]);

    const [sectionStates, setSectionStates] = useState({
        showLastInteraction: false,
        showNextTask: false
    });

    const [connectionFromDB, setConnectionFromDB] = useState(null);
    const [contactList, setContactList] = useState([]);
    const [isContactListOpen, setIsContactListOpen] = useState(false);
    const [contactPermissionDenied, setContactPermissionDenied] = useState(false);
    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);

    const connTypes = isInqOnlyMode
        ? [
            { value: 'Prospect', label: 'Prospect' },
            { value: 'Customer', label: 'Customer' },
            { value: 'Supplier', label: 'Supplier' },
            { value: 'Other', label: 'Other' },
        ]
        : [
            { value: 'Customer', label: 'Customer' },
            { value: 'Supplier', label: 'Supplier' },
        ];

    const recordTypes = useMemo(() => {
        return masterData.recordTypes.map((recordType) => {
            return {
                label: recordType.Typ,
                value: recordType.Typ
            }
        })
    }, [masterData.recordTypes]);


    // useModalBackButton(isOpen, setIsOpen)
    const formRef = useRef(null);

    // Scroll to bottom when opened & scrollToEnd is true
    useEffect(() => {
        if (externalOpen && scrollToEnd && !uiStates.isLoadingConnection && formRef.current) {
            // Use requestAnimationFrame to ensure DOM is updated
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (formRef.current) {
                        formRef.current.scrollTo({
                            top: formRef.current.scrollHeight,
                            behavior: "smooth"
                        });
                    }
                }, 300);
            });
        }
    }, [externalOpen, scrollToEnd, uiStates.isLoadingConnection, contactsData.length]);

    const getInitialFormData = () => {
        const currentUserID = authData?.UserID ? authData.UserID.toString() : "";
        const defaultData = {
            businessName: "",
            name: "",
            designation: "",
            mobile: "",
            email: "",
            city: "",
            state: "",
            country: masterData.agencyData?.Country || "India",
            gstin: "",
            businessPotential: "",
            source: "",
            since: getTodayDate(),
            connType: (initialConnType && ['Prospect', 'Customer', 'Supplier'].includes(initialConnType)) ? initialConnType : 'Prospect',
            segment: "",
            pocUserID: activeConnID ? [] : (currentUserID ? [currentUserID] : []),
            nextTaskPocUserID: activeConnID ? "" : currentUserID,
            interactionBy: activeConnID ? "" : currentUserID,
            nextTaskDT: getTomorrowDate(),
            nextTaskTime: "23:59",
            nextTaskTyp: "", // Changed from nextTaskTyp to use record types
            nextTaskNotes: "",
            notes: "",
            tags: "",
            recordType: "", // For last interaction record type
            recordDate: getTodayDate(),
            recordTime: new Date().toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }),
            isLocationReq: false,
            regionID: 0,
            attachment: '',
            attachmentName: "",
        };

        if (masterData.extraFields && masterData.extraFields.length > 0) {
            masterData.extraFields.forEach(fieldName => {
                const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                defaultData[fieldId] = "";
            });
        }

        // Initialize Thread Custom Fields (for Inquiry - Type 1)
        const inqThreadTypeID = 1;
        const inqExtraFields = (masterData.thrExtraFields || []).find(t => t.TID === inqThreadTypeID);
        if (inqExtraFields && inqExtraFields.Fields) {
            let fieldsArray = [];
            try {
                fieldsArray = JSON.parse(inqExtraFields.Fields);
            } catch (e) { fieldsArray = [] }

            fieldsArray.forEach(field => {
                if (!field.IsD) {
                    // Use a prefix to avoid collision with Contact fields
                    defaultData[`thr_${field.F.toLowerCase().replace(/\s+/g, '')}`] = "";
                }
            });
        }

        if (uiStates.fetchedConnectionData) {
            const processedData = { ...defaultData, ...uiStates.fetchedConnectionData };

            ['pocUserID', 'nextTaskPocUserID', 'interactionBy'].forEach(field => {
                if (processedData[field] === 0 || processedData[field] === "0") {
                    processedData[field] = "";
                } else if (processedData[field]) {
                    processedData[field] = processedData[field].toString();
                }
            });

            return processedData;
        }
        // Apply prefilled data if provided
        if (prefilledData) {
            return { ...defaultData, ...prefilledData };
        }

        return defaultData;
    };

    const handleDeleteContact = (indexToDelete) => {
        if (editMode === 'CONTACTS_ONLY' && selectedContactID) {
            deleteContactDirectly(indexToDelete);
        } else {
            setContactsData(prev => prev.filter((_, index) => index !== indexToDelete));
        }
    };

    const deleteContactDirectly = async (indexToDelete) => {
        try {
            const contactToRemove = contactsData[indexToDelete];
            if (!contactToRemove?.CID) {
                toast.error("Unable to delete: Contact ID not found");
                return;
            }

            setUIStates(prev => ({ ...prev, isLoadingConnection: true }));

            // Get current connection data
            const currentConnection = connectionFromDB || getConnectionByID(activeConnID);
            if (!currentConnection) {
                toast.error("Unable to load connection data");
                return;
            }

            // Build EDIT payload with the contact removed from contacts array
            const primaryContact = contactsData.find(contact => contact.isPrimary) || contactsData[0];

            // Filter out the deleted contact
            const remainingContacts = contactsData
                .filter((_, index) => index !== indexToDelete && !contactsData[index].isPrimary)
                .map((contact) => ({
                    CID: contact.CID || 0,
                    CNm: contact.name,
                    MNo: contact.mobile,
                    Eml: contact.email,
                    Dsgn: contact.designation,
                    CC: contact.countryCode || "91",
                    isPrimary: false
                }));

            // Build full EDIT payload
            const apiData = {
                connID: activeConnID,
                businessName: currentConnection.BizName || formData.businessName,
                name: primaryContact?.name || currentConnection.ContactName || "",
                mobile: primaryContact?.mobile || currentConnection.Mobile || "",
                cc: uiStates.selectedCountryCode || '91',
                email: primaryContact?.email || currentConnection.Email || "",
                city: currentConnection.City || formData.city || "",
                state: currentConnection.State || formData.state || "",
                country: currentConnection.Country || formData.country || "India",
                countryID: countries.find(c => c.Country === (currentConnection.Country || formData.country))?.CountryID || 1,
                gstin: currentConnection.TaxID || formData.gstin || "",
                businessPotential: currentConnection.Potential || formData.businessPotential || "",
                source: currentConnection.Source || formData.source || "",
                since: currentConnection.SinceDT ? format(new Date(currentConnection.SinceDT), "dd-MMM-yy") : formData.since || "",
                segment: currentConnection.Segment || formData.segment || "",
                connType: currentConnection.ConnType || formData.connType || "",
                pocUserID: currentConnection.POCUserID ? currentConnection.POCUserID.toString() : "",
                isSendEmail: formData.isSendEmail || false,
                regionID: currentConnection.RegionID || formData.regionID || 0,
                notes: currentConnection.Notes || formData.notes || "",
                tags: currentConnection.Tags || formData.tags || "",
                customizedFields: JSON.stringify([]),
                nextTaskPocUserID: null,
                nextTaskDT: null,
                nextTaskTime: null,
                nextTaskTyp: null,
                nextTaskNotes: null,
                recordDate: null,
                recordTime: null,
                recordType: null,
                interactionBy: null,
                recordNotes: null,
                threadID: 0,
                thrProduct: "",
                thrModel: "",
                thrQty: "",
                thrPotential: 0,
                thrDueDT: null,
                thrNotes: "",
                reference: "",
                thrStageID: 0,
                thrCustomFields: [],
                contacts: remainingContacts
            };

            const response = await manageConnectionAPI(apiData, 'EDIT');

            if (response) {
                setContactsData(prev => prev.filter((_, index) => index !== indexToDelete));
                // toast.success(`${contactToRemove.name} deleted.`);
                setIsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting contact:", error);
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : error.message || 'Failed to delete contact';
            toast.error(errorMessage);
        } finally {
            setUIStates(prev => ({ ...prev, isLoadingConnection: false }));
        }
    };

    const resetAllStates = useCallback(() => {
        const countryCode = getCountryCode();
        setInternalConnID(null);
        setSavedThreadID(null);
        setIsSteptwo(false);
        setFormData({
            connectionCategory: "Business",
            recordType: "",
            recordDate: getTodayDate(),
            recordTime: new Date().toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }),
            interactionBy: "",
            recordNotes: "",
            businessName: "",
            name: "",
            mobile: "",
            email: "",
            city: "",
            state: "",
            country: masterData.agencyData?.Country || "India",
            gstin: "",
            businessPotential: "",
            source: "",
            since: "",
            connType: (initialConnType && ['Prospect', 'Customer', 'Supplier'].includes(initialConnType)) ? initialConnType : 'Prospect',
            segment: "",
            pocUserID: authData?.UserID ? [authData.UserID.toString()] : [],
            nextTaskPocUserID: "",
            nextTaskDT: getTomorrowDate(), // Defaults to tomorrow
            // nextTaskTime: getRoundedUpTime(),
            nextTaskTime: "23:59",
            nextTaskTyp: "", // Use recordTypes
            nextTaskNotes: "",
            notes: "",
            tags: "",
            isLocationReq: false,
            regionID: 0,
            attachment: '',
            attachmentName: "",
        });

        setUIStates(prev => ({
            ...prev,
            selectedFields: ["name", "businessName", "mobile", "email"],
            selectedCountryCode: countryCode,
            selectedField: "",
            fetchedConnectionData: null,
            showMoreDetails: false, // Ensure it resets to collapsed
        }));

        setContactsData([{ name: "", mobile: "", email: "", designation: "", countryCode: countryCode, isPrimary: true }]);
        setSectionStates({ showLastInteraction: false, showNextTask: false });
        setContactList([]);
        setIsContactListOpen(false);
    }, [connID, masterData.agencyData?.Country, initialConnType]);

    useEffect(() => {
        if (!isOpen) { // Reset whenever dialog closes
            const timer = setTimeout(() => {
                resetAllStates();
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [isOpen, resetAllStates]);

    // Set region after new region added
    useEffect(() => {
        if (selectedRegion) {
            const rID = masterData?.regions.find(rgn => rgn.Rgn == selectedRegion)?.RID || 0;
            setFormData((prev) => ({ ...prev, regionID: rID }))
            setSelectedRegion('')
        }
    }, [selectedRegion])

    useEffect(() => {
        if (isOpen && !connID) {
            const countryCode = getCountryCode();
            setUIStates(prev => ({
                ...prev,
                selectedCountryCode: countryCode
            }));
        }
    }, [isOpen, connID]);

    // Transform database connection data to form format
    const transformConnectionData = (connectionFromDB) => {
        let connType = "";
        if (connectionFromDB.ConnType) {
            const dbConnType = connectionFromDB.ConnType.toString().toUpperCase();
            const types = [];
            if (dbConnType.includes("P")) types.push("Prospect");
            if (dbConnType.includes("C")) types.push("Customer");
            if (dbConnType.includes("S")) types.push("Supplier");
            if (dbConnType.includes("O")) types.push("Other");
            connType = types.join(",");
        }

        // Robustly find email
        let pEmail = connectionFromDB.Email || connectionFromDB.Eml || connectionFromDB.email || "";
        if (!pEmail && connectionFromDB.Contacts) {
            try {
                const c = typeof connectionFromDB.Contacts === 'string' ? JSON.parse(connectionFromDB.Contacts) : connectionFromDB.Contacts;
                if (Array.isArray(c) && c.length > 0) pEmail = c[0].Eml || c[0].Email || c[0].email || "";
            } catch (e) { }
        }

        const baseData = {
            connID: connectionFromDB.ConnID,
            businessName: connectionFromDB.BizName,
            name: connectionFromDB.ContactName,
            designation: connectionFromDB.Designation || "",
            mobile: connectionFromDB.Mobile,
            email: pEmail,
            changeType: connID ? 'EDIT' : 'ADD',
            city: connectionFromDB.City,
            state: connectionFromDB.State,
            country: connectionFromDB.Country,
            connType: connType,
            gstin: connectionFromDB.TaxID,
            businessPotential: connectionFromDB.Potential,
            source: connectionFromDB.Source,
            since: connectionFromDB.SinceDT ? format(new Date(connectionFromDB.SinceDT), "dd-MMM-yy") : '',
            segment: connectionFromDB.Segment,
            pocUserID: connectionFromDB.POCUserIDs ? connectionFromDB.POCUserIDs.split(',').map(id => id.trim()) : (connectionFromDB.POCUserID ? [connectionFromDB.POCUserID.toString()] : []),
            nextTaskPocUserID: connectionFromDB.POCUserID,
            nextTaskDT: isSteptwo ? getTomorrowDate() : (connectionFromDB.NextTaskDT ? format(new Date(connectionFromDB.NextTaskDT), "dd-MMM-yy") : ''),
            nextTaskTime: connectionFromDB.NextTaskDT ? new Date(connectionFromDB.NextTaskDT).toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }) : '',
            nextTaskTyp: connectionFromDB.NextTaskType, // This should map to recordTypes
            nextTaskNotes: connectionFromDB.NextTask,
            notes: connectionFromDB.Notes,
            tags: connectionFromDB.Tags,
            regionID: connectionFromDB?.RegionID,
            attachment: connectionFromDB.ContactCard || connectionFromDB.Attachment || connectionFromDB.attachment || "",
            attachmentName: (connectionFromDB.ContactCard || connectionFromDB.Attachment || connectionFromDB.attachment) ? "Business Card" : ""
        };

        try {
            if (connectionFromDB.CustomFields && connectionFromDB.CustomFields.trim() !== '') {
                const customFields = JSON.parse(connectionFromDB.CustomFields);
                if (Array.isArray(customFields)) {
                    customFields.forEach(field => {
                        const fieldName = field.F;
                        if (fieldName) {
                            const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                            baseData[fieldId] = field.V || "";
                        }
                    });
                }
            }
        } catch (error) {
            console.error("Error parsing CustomFields:", error);
        }

        if (masterData.extraFields && masterData.extraFields.length > 0) {
            masterData.extraFields.forEach(fieldName => {
                const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                if (baseData[fieldId] === undefined) {
                    baseData[fieldId] = connectionFromDB[fieldName] || "";
                }
            });
        }

        return baseData;
    };
    // Handle contact picker functionality for mobile devices
    const openContactPicker = async () => {
        if (!isContactPickerSupported()) {
            toast.error("Contact picker not supported on this device");
            return null;
        }

        try {
            window.contacts = null;
            setContactPermissionDenied(false);

            window.location.href = "requestcontactpermission://";

            setTimeout(() => {
                window.location.href = "readcontacts://";
            }, 300);

            const maxAttempts = 16; // ~8s at 500ms
            let attempts = 0;

            const check = setInterval(() => {
                attempts += 1;

                if (typeof window.contacts !== "undefined" && window.contacts !== null && window.contacts !== "") {
                    clearInterval(check);

                    let parsedContacts = [];
                    try {
                        parsedContacts = typeof window.contacts === 'string'
                            ? JSON.parse(window.contacts)
                            : window.contacts;
                    } catch (e) {
                        console.error("JSON Parse failed for contacts:", e);
                    }

                    if (parsedContacts && parsedContacts.length > 0) {
                        setContactList(parsedContacts);
                        setContactPermissionDenied(false);
                    } else {
                        setContactList([]);
                        setContactPermissionDenied(isIOSDevice());
                    }
                    setIsContactListOpen(true);
                    window.contacts = null; // Clear after use
                } else if (attempts >= maxAttempts) {
                    // No response at all within the timeout window - most likely
                    // contacts permission was denied and iOS never populated window.contacts.
                    clearInterval(check);
                    setContactList([]);
                    setContactPermissionDenied(isIOSDevice());
                    setIsContactListOpen(true);
                }
            }, 500);
            return null;
        } catch (error) {
            console.log('Contact picker error:', error);
            return null;
        }
    };

    // Re-trigger the native permission prompt (used when contacts came back empty on iOS)
    const retryContactPermission = () => {
        openContactPicker();
    };

    // Parse phone number and extract country code
    const parsePhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return { countryCode: undefined, number: '' }

        const cleanInput = phoneNumber.replace(/\D/g, '')
        const parsed = parsePhoneNumberFromString('+' + cleanInput)

        if (parsed && parsed.isValid()) {
            const isoCode = parsed.country
            const nationalNumber = parsed.nationalNumber

            const countryMatch = countries.find(
                (c) => c.CountryCode.toUpperCase() === isoCode
            )

            return {
                countryCode: countryMatch?.CC,
                country: countryMatch?.Country,
                number: nationalNumber
            }
        }

        const fallbackNumber = cleanInput.slice(-10)
        const defaultCountry =
            countries.find((c) => c.Country === formData.country) ||
            countries.find((c) => c.CC === '91')

        return {
            countryCode: defaultCountry?.CC,
            number: fallbackNumber
        }
    }

    // Populate form with selected contact data
    const populateFormWithContact = (contactData) => {
        if (!contactData) return;

        const updates = {};

        if (contactData.name) {
            updates.name = contactData.name;
        }

        // Logic to extract the best mobile number
        let selectedPhone = contactData.phone;

        // If the native bridge provides multiple numbers, prioritize "mobile"
        if (contactData.phoneNumbers && Array.isArray(contactData.phoneNumbers)) {
            const mobileEntry = contactData.phoneNumbers.find(p =>
                p.label?.toLowerCase().includes('mobile') ||
                p.type?.toLowerCase().includes('mobile') ||
                p.label?.toLowerCase().includes('cell')
            );
            if (mobileEntry) {
                selectedPhone = mobileEntry.number || mobileEntry.value;
            }
        }

        if (selectedPhone) {
            const { countryCode, country, number } = parsePhoneNumber(selectedPhone);
            updates.mobile = number;

            if (countryCode) {
                setUIStates(prev => ({ ...prev, selectedCountryCode: countryCode }));
                updates.country = country;
                if (country !== "India") {
                    updates.state = "";
                }
            }
        }

        if (contactData.email && contactData.email !== '-') {
            updates.email = contactData.email;
        } else {
            updates.email = ''
        }

        if (contactData.org && contactData.org !== '-') {
            updates.businessName = contactData.org;
        } else {
            updates.businessName = ''
        }

        if (Object.keys(updates).length > 0) {
            setFormData(prev => ({ ...prev, ...updates }));
            // toast.success("Contact information selected.");
        }
    };

    // Handle contact picker button click
    const handleContactPickerClick = async () => {
        if (!isMobileDevice()) {
            toast.info("Contact picker is only available on mobile devices");
            return;
        }

        if (!isContactPickerSupported()) {
            toast.error("Contact picker not supported on this device");
            return;
        }

        try {
            const contactData = await openContactPicker();
            if (contactData) {
                populateFormWithContact(contactData);
            }
        } catch (error) {
            console.error('Error in handleContactPickerClick:', error);
            // Fallback to contact list dialog if native picker fails
            setIsContactListOpen(true);
        }
    };

    const isConnectionTypeSelected = (type) => {
        if (!formData.connType) return false;
        const types = formData.connType.split(',').map(t => t.trim());
        return types.includes(type);
    };

    // Function which will handle Email shared checkbox
    const handleEmailSharedCheckbox = (checked) => {
        setFormData(prev => ({ ...prev, isSendEmail: checked }));
    }

    const handleConnectionTypeChange = (type, checked) => {

        // If InqOnlyMode → only one selection allowed
        if (isInqOnlyMode) {
            setFormData(prev => ({
                ...prev,
                connType: checked ? type : '',
            }));
            return;
        }

        // Normal checkbox behavior (multi-select)
        let currentTypes = formData.connType
            ? formData.connType.split(',').map(t => t.trim()).filter(t => t)
            : [];

        if (checked) {
            if (!currentTypes.includes(type)) currentTypes.push(type);
        } else {
            currentTypes = currentTypes.filter(t => t !== type);
        }

        const newConnType = currentTypes.join(',');
        setFormData(prev => ({ ...prev, connType: newConnType }));
    };

    const isFieldRequired = (fieldID) => {
        const alwaysRequired = ["connType"];
        if (alwaysRequired.includes(fieldID)) return true;

        const conditionalRequirements = {
            recordDate: sectionStates.showLastInteraction && !connID,
            recordTime: sectionStates.showLastInteraction && !connID,
            recordNotes: sectionStates.showLastInteraction && !connID,
            recordType: sectionStates.showLastInteraction && !connID,
            nextTaskDT: sectionStates.showNextTask && !connID,
            nextTaskTime: sectionStates.showNextTask && !connID,
            nextTaskTyp: sectionStates.showNextTask && !connID,
            nextTaskNotes: sectionStates.showNextTask && !connID,
        };

        return conditionalRequirements[fieldID] || false;
    };

    const isFieldDisabled = (fieldID) => {
        const lastInteractionFields = ["recordDate", "recordType", "recordTime", "interactionBy", "recordNotes"];
        const nextTaskFields = ["nextTaskDT", "nextTaskTime", "nextTaskTyp", "nextTaskNotes", "nextTaskPocUserID"];

        if (lastInteractionFields.includes(fieldID)) {
            return !sectionStates.showLastInteraction;
        }

        if (nextTaskFields.includes(fieldID)) {
            return !sectionStates.showNextTask;
        }

        return false;
    };

    // Validate form data before submission
    const validateFormData = () => {
        const validationRules = [
            {
                field: 'businessName',
                condition: !formData.businessName?.trim() && !formData.name?.trim(),
                message: 'Please enter either a business name or contact name.',
                description: 'Business Name or Contact Name is required.'
            },
            {
                field: 'connType',
                condition: !formData.connType && !isFirstConnection,
                message: 'Please select a contact type.',
                description: 'Contact type is required.'
            },
            // {
            //     field: 'name',
            //     condition: !formData.name?.trim(),
            //     message: 'Please enter a name.',
            //     description: 'Name is required.'
            // },
            {
                field: 'email',
                condition: formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
                message: 'Please enter a valid email address.',
                description: 'Invalid email format.'
            },
            {
                field: 'recordDate',
                condition: sectionStates.showLastInteraction && !connID && !formData.recordDate?.trim(),
                message: 'Please select last interaction date.',
                description: 'Last interaction date is required when last interaction is enabled.'
            },
            {
                field: 'recordTime',
                condition: sectionStates.showLastInteraction && !connID && !formData.recordTime?.trim(),
                message: 'Please select interaction time.',
                description: 'Interaction time is required when last interaction is enabled.'
            },
            {
                field: 'recordType',
                condition: sectionStates.showLastInteraction && !connID && !formData.recordType?.trim(),
                message: 'Please select a record type.',
                description: 'Record type is required when last interaction is enabled.'
            },
            {
                field: 'recordNotes',
                condition: sectionStates.showLastInteraction && !connID && !formData.recordNotes?.trim(),
                message: 'Please enter interaction notes.',
                description: 'Interaction notes are required when last interaction is enabled.'
            },
            {
                field: 'nextTaskDT',
                condition: sectionStates.showNextTask && !connID && !formData.nextTaskDT?.trim(),
                message: 'Please select next task date.',
                description: 'Next Task date is required when next Task is enabled.'
            },
            {
                field: 'nextTaskTyp',
                condition: sectionStates.showNextTask && !connID && !formData.nextTaskTyp?.trim(),
                message: 'Please select a task type.',
                description: 'Task type is required when next task is enabled.'
            },
            {
                field: 'nextTaskTyp',
                condition: sectionStates.showNextTask && !connID && !recordTypes.some(type => type.value === formData.nextTaskTyp),
                message: 'The entered task type does not exist in the list.',
                description: 'Please select a valid task type from the list.'
            },
            {
                field: 'nextTaskNotes',
                condition: sectionStates.showNextTask && !connID && (!formData.nextTaskNotes?.trim()),
                message: 'Please enter next task notes.',
                description: 'Next Task notes are required when next Task is enabled.'
            }
        ];

        for (const rule of validationRules) {
            if (rule.condition) {
                return {
                    isValid: false,
                    field: rule.field,
                    message: rule.message,
                    description: rule.description
                };
            }
        }
        return { isValid: true };
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (fieldName, value) => {
        if (value === 'add') {
            // Handle adding new items - Updated mapping
            const fieldToMasterTypeMap = {
                'source': 'sources',
                'segment': 'segments',
                'recordType': 'recordTypes',
                'nextTaskTyp': 'recordTypes'
            };

            const masterType = fieldToMasterTypeMap[fieldName];
            if (masterType) {
                setUIStates(prev => ({ ...prev, selectedField: fieldName }));
                setMasterDialogType(masterType);
                setDialogStates(prev => ({ ...prev, masterDialog: true }));
                return;
            }
        }

        if (fieldName === 'pocUserID') {
            // Value is already an array from MultiSelect
            setFormData(prev => ({ ...prev, [fieldName]: value }));
        } else if (['interactionBy', 'nextTaskPocUserID'].includes(fieldName)) {
            setFormData(prev => ({ ...prev, [fieldName]: value === "" ? "" : value }));
        } else if (fieldName === 'city') {
            const stateName = cityToStateMap[value];
            if (stateName) {
                setFormData(prev => ({
                    ...prev,
                    city: value,
                    state: stateName
                }));
            } else {
                setFormData(prev => ({ ...prev, city: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [fieldName]: value }));
        }
    };


    const handleDateChange = (fieldID, date) => {
        const formattedDate = date ? format(date, "dd-MMM-yy") : "";
        setFormData(prev => ({ ...prev, [fieldID]: formattedDate }));
        setUIStates(prev => ({
            ...prev,
            datePickerStates: { ...prev.datePickerStates, [fieldID]: false }
        }));

        if (fieldID === "nextTaskDT" && !connID && formData.nextTaskTime) {
            const validation = validateTaskDateTime(formattedDate, formData.nextTaskTime, true);
            if (!validation.isValid) {
                const futureTimeString = getNextHourTime();
                setFormData(prev => ({ ...prev, nextTaskTime: futureTimeString }));
            }
        }
    };

    // Handle time changes with validation for past/future restrictions
    const handleTimeChange = (fieldName, timeValue) => {
        setFormData(prev => ({ ...prev, [fieldName]: timeValue }));
        const isRecordTime = fieldName === "recordTime";
        const dateField = isRecordTime ? "recordDate" : "nextTaskDT";

        if (!connID && formData[dateField]) {
            const selectedDate = new Date(formData[dateField]);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            selectedDate.setHours(0, 0, 0, 0);
            const isToday = selectedDate.getTime() === today.getTime();

            if (isToday) {
                const [selectedHour, selectedMinute] = timeValue.split(':').map(Number);
                const now = new Date();
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();
                const selectedTimeInMinutes = selectedHour * 60 + selectedMinute;
                const currentTimeInMinutes = currentHour * 60 + currentMinute;

                if (isRecordTime && selectedTimeInMinutes > currentTimeInMinutes) {
                    const currentTimeString = now.toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                    toast.error("Cannot select a future time for Last Interaction.", {
                        description: "The selected time is later than the current time. Setting to current time.",
                    });
                    setFormData(prev => ({ ...prev, [fieldName]: currentTimeString }));
                    return;
                } else if (!isRecordTime && selectedTimeInMinutes < currentTimeInMinutes) {
                    const futureTime = new Date(now.getTime() + 60 * 60 * 1000);
                    const futureTimeString = futureTime.toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                    toast.error("Cannot select a past time for Next Task.", {
                        description: "The selected time is earlier than the current time. Setting to the next available hour.",
                    });
                    setFormData(prev => ({ ...prev, [fieldName]: futureTimeString }));
                    return;
                }
            }

            if (!isRecordTime) {
                const validation = validateTaskDateTime(formData.nextTaskDT, timeValue, true);
                if (!validation.isValid) {
                    toast.error(validation.message, {
                        description: validation.description
                    });
                    const futureTimeString = getNextHourTime();
                    setFormData(prev => ({ ...prev, [fieldName]: futureTimeString }));
                    return;
                }
            }
        }
    };

    // Trigger alert for cold contact status
    const handleToggleColdContact = () => {
        if (!connID) {
            toast.error('Please save the contact first before marking as cold');
            return;
        }
        setDialogStates(prev => ({ ...prev, coldAlert: true }));
    };

    // Execute toggle cold contact status
    const executeToggleColdContact = async () => {
        try {
            const currentConnection = connectionFromDB || getConnectionByID(connID);
            const newColdStatus = (currentConnection?.IsCold === 1 || currentConnection?.IsCold === true) ? 0 : 1;
            const updateData = {
                ConnID: connID,
                IsCold: newColdStatus
            };

            await manageConnectionAPI(updateData, 'COLD');

            const message = newColdStatus === 1 ? 'Marked as cold contact' : 'Marked as active contact';
            // toast.success(message);
        } catch (error) {
            console.error('Error toggling cold contact status:', error);
            toast.error('Failed to update contact status');
        } finally {
            setDialogStates(prev => ({ ...prev, coldAlert: false }));
        }
    };


    // Save connection data with validation and API call
    const handleSaveConnection = async () => {
        const validation = validateFormData();
        if (!validation.isValid) {
            toast.error(validation.message, {
                description: validation.description
            });
            return;
        }

        if (connID) {
            const primaryContact = contactsData.find(contact => contact.isPrimary);
            // if (!primaryContact || !primaryContact.name.trim()) {
            //     toast.error("Contact person name is required.");
            //     return;
            // }
        }

        let apiConnType = "";
        if (formData.connType) {
            const types = formData.connType.split(',').map(t => t.trim());
            const typeLetters = [];
            if (types.includes('Prospect')) typeLetters.push('P');
            if (types.includes('Customer')) typeLetters.push('C');
            if (types.includes('Supplier')) typeLetters.push('S');
            if (types.includes('Other')) typeLetters.push('O');
            apiConnType = typeLetters.join('');
        } else if (isFirstConnection) {
            apiConnType = isInqOnlyMode ? 'P' : 'C';
        }

        const primaryContact = contactsData.find(contact => contact.isPrimary) || contactsData[0];

        const customFieldsArray = [];
        if (masterData.extraFields && masterData.extraFields.length > 0) {
            masterData.extraFields.forEach((fieldName, index) => {
                const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                const fieldValue = formData[fieldId];

                if (fieldValue && fieldValue.trim() !== '') {
                    customFieldsArray.push({
                        FID: index + 1,
                        F: fieldName,
                        V: fieldValue.trim()
                    });
                }
            });
        }

        // Process Thread Custom Fields (Inquiry Type = 1)
        const thrCustomFieldsArray = [];
        if (isInquiryChecked && !connID) {
            const inqThreadTypeID = 1;
            const inqExtraFields = (masterData.thrExtraFields || []).find(t => t.TID === inqThreadTypeID);
            if (inqExtraFields && inqExtraFields.Fields) {
                let fieldsArray = [];
                try {
                    fieldsArray = JSON.parse(inqExtraFields.Fields);
                } catch (e) { fieldsArray = [] }

                fieldsArray.forEach(field => {
                    if (!field.IsD) {
                        const fieldId = `thr_${field.F.toLowerCase().replace(/\s+/g, '')}`;
                        const fieldValue = formData[fieldId];

                        if (fieldValue && fieldValue.trim() !== '') {
                            thrCustomFieldsArray.push({
                                FID: field.FID,
                                F: field.F,
                                V: fieldValue.trim()
                            });
                        }
                    }
                });
            }
        }

        // Validate Inquiry Data if checked
        if (isInquiryChecked && !connID) {
            // if (!inquiryData.product) {
            //     toast.error("Please enter a product for the inquiry.");
            //     return;
            // }
            if (!inquiryData.dueDT) {
                toast.error("Please select a due date for the inquiry.");
                return;
            }
        }

        let uploadedFileName = '';
        if (formData.attachment && formData.attachment instanceof File) {
            try {
                let fileToUpload = formData.attachment;
                if (formData.attachment.type.startsWith('image/')) {
                    fileToUpload = await compressImage(formData.attachment);
                }

                const uploadResult = await apiService.uploadFile(
                    fileToUpload,
                    "Connections",
                    "both"
                );
                if (uploadResult.error) {
                    toast.error(uploadResult.message);
                    return;
                }
                uploadedFileName = uploadResult.data.relativePath;
            } catch (uploadError) {
                console.error('File upload error:', uploadError);
                toast.error('Failed to upload file');
                return;
            }
        } else if (typeof formData.attachment === 'string') {
            // Existing business card path (untouched), or '' if the user removed it.
            uploadedFileName = formData.attachment;
        }

        const apiData = {
            connID: formData.connID || 0,
            businessName: formData.businessName,
            name: connID ? (primaryContact?.name || "") : formData.name,
            designation: connID ? (primaryContact?.designation || "") : formData.designation,
            mobile: connID ? (primaryContact?.mobile || "") : formData.mobile,
            cc: uiStates.selectedCountryCode ? countries.find(c => c.CC === uiStates.selectedCountryCode)?.CC || '91' : '91',
            email: connID ? (primaryContact?.email || "") : formData.email || primaryContact?.email || "",
            city: formData.city,
            state: formData.state,
            country: formData.country || "India",
            countryID: countries?.find(c => c.CC === uiStates.selectedCountryCode)?.CountryID || 0,
            gstin: formData.gstin,
            businessPotential: formData.businessPotential,
            source: formData.source,
            since: formData.since,
            segment: formData.segment,
            connType: apiConnType,
            pocUserID: Array.isArray(formData.pocUserID) ? formData.pocUserID.join(',') : (formData.pocUserID || "0"),
            isSendEmail: formData.isSendEmail || false,
            regionID: formData.regionID,

            // Next Task fields - only pass if checkbox is checked AND not in edit mode
            nextTaskPocUserID: (sectionStates.showNextTask && !connID) ?
                ((!formData.nextTaskPocUserID || formData.nextTaskPocUserID === "") ? 0 : parseInt(formData.nextTaskPocUserID)) : null,
            nextTaskDT: (sectionStates.showNextTask && !connID) ? formData.nextTaskDT : null,
            nextTaskTime: (sectionStates.showNextTask && !connID) ? formData.nextTaskTime : null,
            nextTaskTyp: (sectionStates.showNextTask && !connID) ? formData.nextTaskTyp : null,
            nextTaskNotes: (sectionStates.showNextTask && !connID) ? formData.nextTaskNotes : null,

            // Last Interaction fields - only pass if checkbox is checked AND not in edit mode  
            recordDate: (sectionStates.showLastInteraction && !connID) ? formData.recordDate : null,
            recordTime: (sectionStates.showLastInteraction && !connID) ? formData.recordTime : null,
            recordType: (sectionStates.showLastInteraction && !connID) ? formData.recordType : null,
            interactionBy: (sectionStates.showLastInteraction && !connID) ?
                ((!formData.interactionBy || formData.interactionBy === "") ? 0 : parseInt(formData.interactionBy)) : null,
            recordNotes: (sectionStates.showLastInteraction && !connID) ? formData.recordNotes : null,

            notes: formData.notes,
            tags: formData.tags,
            customizedFields: JSON.stringify(customFieldsArray),
            attachment: uploadedFileName || "",

            // Pass Inquiry data to backend to create thread in same transaction
            threadID: savedThreadID || 0,
            thrProduct: (isInquiryChecked && !connID) ? inquiryData.product : "",
            thrModel: (isInquiryChecked && !connID) ? (inquiryData.model || "") : "",
            thrQty: (isInquiryChecked && !connID) ? (inquiryData.quantity || "") : "",
            thrPotential: (isInquiryChecked && !connID) ? (inquiryData.businessValue || 0) : 0,
            thrDueDT: (isInquiryChecked && !connID) ? inquiryData.dueDT : null,
            thrNotes: (isInquiryChecked && !connID) ? (inquiryData.notes || "") : "",
            reference: (isInquiryChecked && !connID) ? (inquiryData.reference || "") : "",
            thrStageID: (isInquiryChecked && !connID) ? (inquiryData.stageID || 0) : 0,  // New: Inquiry stage
            thrCustomFields: (isInquiryChecked && !connID) ? thrCustomFieldsArray : [],

            ...(connID && {
                contacts: contactsData
                    .filter(contact => !contact.isPrimary)
                    .map((contact, index) => ({
                        CID: contact.CID || 0,
                        CNm: contact.name,
                        MNo: contact.mobile,
                        Eml: contact.email,
                        Dsgn: contact.designation,
                        CC: contact.countryCode || "91",
                        isPrimary: false
                    }))
            })
        };
        try {
            const operation = apiData.connID && apiData.connID > 0 ? 'EDIT' : 'ADD';
            const result = await manageConnectionAPI(apiData, operation);

            if (isInquiryChecked && !activeConnID) {
                if ((isFirstConnection || !isMoreDetailsChecked)) {
                    // Skip Step 2 if first connection OR more details not checked (except for FORCE_INQUIRY)
                    // toast.success("Contact and Inquiry saved.");
                    setIsSteptwo(false);
                    setIsOpen(false);
                    if (result.connectionData && result.connectionData.ConnID) {
                        setSavedConnID(result.connectionData.ConnID);
                    }

                    setIsDetailOpen(true);

                    setFormData(getInitialFormData());
                    setContactsData([{ name: "", mobile: "", email: "", designation: "", countryCode: "91", isPrimary: true }]);
                } else {
                    // Go to Step 2 (Edit Mode) - ALWAYS
                    // toast.success("Contact and Inquiry saved. You can now add more details.");

                    if (result.connectionData && result.connectionData.ConnID) {
                        setInternalConnID(result.connectionData.ConnID);
                    }
                    setUIStates(prev => ({ ...prev, showMoreDetails: true })); // Expand form for Step 2
                    setIsSteptwo(true);
                    // Default Enable Next Task in Step 2 (Only if applicable)
                    const shouldShowNextTask = calledFrom !== 'THREAD_ENTRY' && calledFrom !== 'FORCE_INQUIRY' && threadTypeID !== 2 && !isBasic;
                    setSectionStates(prev => ({ ...prev, showNextTask: shouldShowNextTask }));
                    if (shouldShowNextTask) {
                        setFormData(prev => ({ ...prev, nextTaskDT: getTomorrowDate() }));
                    }

                    setFormData(prev => ({ ...prev, nextTaskDT: getTomorrowDate() }));

                    // Extract ThreadID specifically for this flow
                    if (result.updatePackage) {
                        try {
                            const up = typeof result.updatePackage === 'string' ? JSON.parse(result.updatePackage) : result.updatePackage;
                            // The Thread entry usually comes back in the Table array. 
                            // We need to find the table that has 'Threads' data or check the RetData.
                            // Based on standard API pattern for 'ADD', the first table often contains the return data.
                            if (up.Table && up.Table.length > 0) {
                                const retDataStr = up.Table[0].RetData;
                                if (retDataStr) {
                                    const retData = JSON.parse(retDataStr);
                                    if (retData && retData.length > 0 && retData[0].ThreadID) {
                                        setSavedThreadID(retData[0].ThreadID);
                                    }
                                }
                            }
                        } catch (e) {
                            console.error("Error extracting ThreadID:", e);
                        }
                    }
                }
            } else {
                // Case: Normal Contact Flow

                if (operation === 'ADD' && result.connectionData?.ConnID) {
                    if (isFirstConnection || (!isMoreDetailsChecked)) {
                        // Skip Step 2 if basic, first connection, threadType 2, OR more details not checked (except for FORCE_INQUIRY)
                        // toast.success("Contact saved.");
                        setIsSteptwo(false);
                        setIsOpen(false);
                        if (result.connectionData && result.connectionData.ConnID) {
                            setSavedConnID(result.connectionData.ConnID);
                        }

                        // Only open detail if NOT called from ThreadEntry and NOT isBasic
                        if (!isBasic && calledFrom !== 'FORCE_INQUIRY' && !(threadTypeID === 2)) {
                            setIsDetailOpen(true);
                        }

                        setFormData(getInitialFormData());
                        setContactsData([{ name: "", mobile: "", email: "", designation: "", countryCode: "91", isPrimary: true }]);
                    } else {
                        // STEP 1 -> STEP 2: Created new contact. Always go to Step 2.
                        // toast.success(isInquiryChecked ? "Contact saved. You can now add more details." : "Contact saved. You can now add more details.");
                        setInternalConnID(result.connectionData.ConnID);
                        setUIStates(prev => ({ ...prev, showMoreDetails: true })); // Expand form for Step 2
                        setIsSteptwo(true);

                        // Default Enable Next Task in Step 2 (Only if applicable)
                        const shouldShowNextTask = calledFrom !== 'THREAD_ENTRY' && calledFrom !== 'FORCE_INQUIRY' && threadTypeID !== 2 && !isBasic;
                        setSectionStates(prev => ({ ...prev, showNextTask: shouldShowNextTask }));
                        if (shouldShowNextTask) {
                            setFormData(prev => ({ ...prev, nextTaskDT: getTomorrowDate() }));
                        }
                        // Attempt to extract ThreadID from response
                        if (result.updatePackage) {
                            try {
                                const up = typeof result.updatePackage === 'string' ? JSON.parse(result.updatePackage) : result.updatePackage;
                                if (up.Table && up.Table[0] && up.Table[0].RetData) {
                                    const retData = JSON.parse(up.Table[0].RetData);
                                    if (retData[0] && retData[0].ThreadID) {
                                        console.log("ThreadID extracted:", retData);
                                        setSavedThreadID(retData[0].ThreadID);
                                    }
                                }
                            } catch (e) {
                                console.error("Error extracting ThreadID:", e);
                            }
                        }
                    }
                }
                else if (operation === 'EDIT') {
                    // STEP 2 -> STEP 3: Editing.

                    if (!connID && internalConnID) {
                        // This is the "Second Save" (Flow: Create -> Edit -> Save)
                        // Action: Close Dialog -> Open Connection Details
                        // toast.success("Contact updated.");

                        // Reset Step 2 flag first
                        setIsSteptwo(false);

                        // Then close dialog
                        setIsOpen(false);
                        setSavedConnID(internalConnID);
                        // Only open detail if NOT called from ThreadEntry
                        if (calledFrom !== 'FORCE_INQUIRY' && calledFrom !== 'THREAD_ENTRY' && !isBasic) {
                            setIsDetailOpen(true);
                        }

                        // Reset form for next time
                        setFormData(getInitialFormData());
                        setContactsData([{ name: "", mobile: "", email: "", designation: "", countryCode: "91", isPrimary: true }]);

                        // Call onConnectionAdded with the saved connection data after Step 2
                        if (onConnectionAdded && calledFrom === 'FORCE_INQUIRY') {
                            onConnectionAdded({ ConnID: internalConnID });
                        }
                    } else {
                        // If Editing existing contact (connID exists), always save and close.
                        if (connID || uiStates.showMoreDetails) {
                            // Finish: Save and Close
                            // toast.success("Contact updated.");
                            setIsOpen(false);
                            setFormData(getInitialFormData());
                            setContactsData([{ name: "", mobile: "", email: "", designation: "", countryCode: "91", isPrimary: true }]);
                        } else
                            // Standard Edit logic with Stepped Flow (Only for new contacts created via Step 1)
                            if (!uiStates.showMoreDetails && ((hasMoreDetailsEnabled || hasConnExtraFields) || ((isMobile && emailPref) || hasConnExtraFields))) {
                                // Step 1 -> Step 2: Expand to show all fields
                                setIsSteptwo(true);
                                // toast.success("Contact updated. You can now add more details.");
                                setUIStates(prev => ({ ...prev, showMoreDetails: true }));
                                // Do NOT close the dialog
                            } else {
                                // Finish: Save and Close
                                // toast.success("Contact updated.");
                                setIsOpen(false);
                                setFormData(getInitialFormData());
                                setContactsData([{ name: "", mobile: "", email: "", designation: "", countryCode: "91", isPrimary: true }]);
                            }
                    }
                }
            }

            if (forceOpen && isMobile) {
                localStorage.removeItem('SignUp');
            }

            // Only call onConnectionAdded if NOT going to Step 2
            // It will be called after Step 2 completion
            // const isGoingToStepTwo = (operation === 'ADD' && !connID && result.connectionData?.ConnID) && !isFirstConnection;
            const isGoingToStepTwo = (operation === 'ADD' && !connID && result.connectionData?.ConnID) && !isFirstConnection && isMoreDetailsChecked;

            if (onConnectionAdded && !isGoingToStepTwo) {
                await onConnectionAdded(result.connectionData);
            }

            if (onConnectionSaved) {
                await onConnectionSaved(result.connectionData);
            }

            setFormData(getInitialFormData());
        } catch (error) {
            console.error("Error saving contact:", error);
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : error.message || 'An error occurred while saving the contact';
            toast.error(errorMessage);
        }
    };

    // Delete connection with confirmation
    const handleDeleteConnection = async () => {
        if (hasActiveTasks) {
            toast.error("This contact has tasks associated with it and cannot be deleted.");
            return;
        }
        try {
            let apiConnType = "";
            if (formData.connType) {
                const types = formData.connType.split(',').map(t => t.trim());
                const typeLetters = [];
                if (types.includes('Prospect')) typeLetters.push('P');
                if (types.includes('Customer')) typeLetters.push('C');
                if (types.includes('Supplier')) typeLetters.push('S');
                apiConnType = typeLetters.join('');
            }

            const deleteApiData = {
                ...formData,
                countryID: countries?.find(c => c.CC === uiStates.selectedCountryCode)?.CountryID || 0,
                connType: apiConnType
            };

            await manageConnectionAPI(deleteApiData, 'DELETE');
            // toast.success("Contact deleted.");
            // REMOVE this line: setDialogStates(prev => ({ ...prev, deleteAlert: false }));
            setIsOpen(false);

            if (parentDialogSetter) {
                parentDialogSetter(false);
            }

            setFormData(getInitialFormData());
            if (onConnectionAdded) {
                onConnectionAdded();
            }
        } catch (error) {
            console.error("Error deleting contact:", error);
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : error.message || 'Failed to delete contact';
            toast.error(errorMessage);
        }
    };

    const handleContactChange = (index, field, value) => {
        setContactsData(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    // Load connection data when editing existing connection
    useEffect(() => {
        if (!activeConnID || !isOpen) {
            setUIStates(prev => ({ ...prev, fetchedConnectionData: null }));
            setConnectionFromDB(null);
            return;
        }

        setUIStates(prev => ({ ...prev, isLoadingConnection: true }));

        const loadConn = async () => {
            try {
                // Try from Redux store first
                let connObj = getConnectionByID(activeConnID);

                // Try from IndexedDB if not found in Redux
                if (!connObj) {
                    try {
                        const conns = await getFromIndexedDB('Conns') || [];
                        connObj = conns.find(c =>
                            c && (
                                String(c.ConnID) === String(activeConnID) ||
                                String(c.NodeID) === String(activeConnID) ||
                                Number(c.ConnID) === Number(activeConnID) ||
                                Number(c.NodeID) === Number(activeConnID)
                            )
                        );
                    } catch (e) {
                        console.error('Error looking up connection in IndexedDB fallback:', e);
                    }
                }

                if (connObj) {
                    setConnectionFromDB(connObj);
                    if (connObj.AgencyID) {
                        apiService.setAgencyContext(connObj.AgencyID);
                    }
                    const transformedData = transformConnectionData(connObj);
                    setUIStates(prev => ({ ...prev, fetchedConnectionData: transformedData }));

                    // Robustly find email
                    let pEmail = connObj.Email || connObj.Eml || connObj.email || "";
                    if (!pEmail && connObj.Contacts) {
                        try {
                            const c = typeof connObj.Contacts === 'string' ? JSON.parse(connObj.Contacts) : connObj.Contacts;
                            if (Array.isArray(c) && c.length > 0) pEmail = c[0].Eml || c[0].Email || c[0].email || "";
                        } catch (e) { }
                    }

                    const primaryContact = {
                        name: connObj.ContactName || "",
                        mobile: connObj.Mobile || "",
                        email: pEmail,
                        designation: connObj.Designation || "",
                        countryCode: connObj.CC || "91",
                        isPrimary: true
                    };

                    let additionalContacts = [];
                    if (connObj.Contacts1) {
                        try {
                            const parsedContacts = typeof connObj.Contacts1 === 'string'
                                ? JSON.parse(connObj.Contacts1)
                                : connObj.Contacts1;

                            if (Array.isArray(parsedContacts)) {
                                additionalContacts = parsedContacts.map(contact => ({
                                    CID: contact.CID || 0,
                                    name: contact.CNm || "",
                                    mobile: contact.MNo || "",
                                    email: contact.Eml || "",
                                    designation: contact.Dsgn || "",
                                    countryCode: contact.CC || "91",
                                    isPrimary: false
                                }));
                            }
                        } catch (error) {
                            console.error("Error parsing additional contacts:", error);
                        }
                    }

                    setContactsData([primaryContact, ...additionalContacts]);
                } else {
                    console.warn(`Contact with ID ${activeConnID} not found`);
                    setUIStates(prev => ({ ...prev, fetchedConnectionData: null }));
                    setConnectionFromDB(null);
                }
            } catch (error) {
                console.error('Error loading contact data:', error);
                setUIStates(prev => ({ ...prev, fetchedConnectionData: null }));
                setConnectionFromDB(null);
            } finally {
                setUIStates(prev => ({ ...prev, isLoadingConnection: false }));
            }
        };

        loadConn();

        return () => {
            apiService.clearAgencyContext();
        };
    }, [activeConnID, isOpen, connectionData]);

    useEffect(() => {
        if (uiStates.fetchedConnectionData) {
            setFormData({ ...getInitialFormData(), ...uiStates.fetchedConnectionData });

            setUIStates(prev => {
                const newState = { ...prev };
                if (uiStates.fetchedConnectionData.country) {
                    const selectedCountry = countries.find(c => c.Country === uiStates.fetchedConnectionData.country);
                    if (selectedCountry) {
                        newState.selectedCountryCode = selectedCountry.CC;
                    }
                }
                // Force show details in Edit Mode so all fields appear immediately
                if (connID) {
                    newState.showMoreDetails = true;
                }
                return newState;
            });
        } else if (!activeConnID && isOpen) {
            if (!prefilledData) {
                setFormData(getInitialFormData());
            }
            else {
                if (!formData.businessName && prefilledData.businessName) {
                    setFormData(prev => ({ ...prev, ...prefilledData }));
                }
            }
        }
    }, [uiStates.fetchedConnectionData, connID, isOpen, initialConnType, prefilledData]);

    useEffect(() => {
        if (uiStates.selectedCountryCode) {
            const countryFromCode = countries.find(c => c.CC === uiStates.selectedCountryCode);
            if (countryFromCode && formData.country !== countryFromCode.Country) {
                setFormData(prev => ({
                    ...prev,
                    country: countryFromCode.Country,
                    state: countryFromCode.Country !== "India" ? "" : prev.state
                }));
            }
        }
    }, [uiStates.selectedCountryCode]);

    useEffect(() => {
        if (formData.country !== "India" && formData.state && !uiStates.isInitialLoad) {
            setFormData(prev => ({ ...prev, state: "" }));
        }

        if (formData.country) {
            const selectedCountry = countries.find(c => c.Country === formData.country);
            if (selectedCountry && uiStates.selectedCountryCode !== selectedCountry.CC) {
                setUIStates(prev => ({ ...prev, selectedCountryCode: selectedCountry.CC }));
            }
        }

        if (uiStates.isInitialLoad) {
            setUIStates(prev => ({ ...prev, isInitialLoad: false }));
        }
    }, [formData.country]);

    useEffect(() => {
        const currentUserID = authData?.UserID ? authData.UserID.toString() : "";

        if (!uiStates.fetchedConnectionData && !connID && currentUserID && uiStates.isInitialLoad) {
            setFormData(prev => {
                const updates = {};

                if (!prev.pocUserID || prev.pocUserID === "0" || prev.pocUserID === 0) {
                    updates.pocUserID = currentUserID;
                }

                if (sectionStates.showLastInteraction && (!prev.interactionBy || prev.interactionBy === "0" || prev.interactionBy === 0)) {
                    updates.interactionBy = currentUserID;
                    // Set current time for last interaction when section becomes active
                    updates.recordTime = new Date().toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                }

                if (sectionStates.showNextTask && (!prev.nextTaskPocUserID || prev.nextTaskPocUserID === "0" || prev.nextTaskPocUserID === 0)) {
                    updates.nextTaskPocUserID = currentUserID;
                }

                return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
            });
        }
    }, [authData, sectionStates.showLastInteraction, sectionStates.showNextTask, uiStates.fetchedConnectionData, connID, uiStates.isInitialLoad]);


    useEffect(() => {
        if (sectionStates.showLastInteraction && !connID) {
            setFormData(prev => ({
                ...prev,
                recordTime: new Date().toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                })
            }));
        }
    }, [sectionStates.showLastInteraction, connID]);

    useEffect(() => {
        if (sectionStates.showNextTask && !connID) {
            setFormData(prev => {
                // Always ensure tomorrow's date is set when enabled, if invalid or empty
                if (!prev.nextTaskDT) {
                    return { ...prev, nextTaskDT: getTomorrowDate() };
                }
                return prev;
            });
        }
    }, [sectionStates.showNextTask, connID]);

    // Load specific contact data when in CONTACTS_ONLY mode
    useEffect(() => {
        if (editMode === 'CONTACTS_ONLY' && selectedContactID && uiStates.fetchedConnectionData && isOpen) {
            // Find the selected contact in the current contactsData
            const selectedContact = contactsData.find(c => c.CID === selectedContactID);

            if (selectedContact) {
                // Contact is already loaded, fields will render from contactsData
                setUIStates(prev => ({ ...prev, isLoadingConnection: false }));
            } else {
                // If contact not found in contactsData, it means data might not be fully loaded
                // This shouldn't happen if properly passed from parent, but as a fallback:
                setUIStates(prev => ({ ...prev, isLoadingConnection: false }));
            }
        }
    }, [editMode, selectedContactID, isOpen, contactsData]);

    const emailPref = useSelector(state => selectConnFieldPref(state, "Email"));
    const connViewTypeID = currentUserHasPermission('ConnViewTypeID');

    // Check if any "More Details" fields are enabled (Location, Business, Other)
    const hasMoreDetailsEnabled = useSelector(state =>
        selectConnFieldPref(state, "City") ||
        selectConnFieldPref(state, "State") ||
        selectConnFieldPref(state, "Country") ||
        selectConnFieldPref(state, "TaxID") ||
        selectConnFieldPref(state, "Potential") ||
        selectConnFieldPref(state, "Source") ||
        selectConnFieldPref(state, "Since") ||
        selectConnFieldPref(state, "Segement") ||
        selectConnFieldPref(state, "Notes") ||
        selectConnFieldPref(state, "Tags") ||
        selectConnFieldPref(state, "POC")
    );

    // Check if any "Inquiry" fields are enabled
    const inqThreadTypeID = 1; // Standard Inquiry ID
    const hasThreadDetailsEnabled = useSelector(state =>
        selectThreadFieldPref(state, 'POC', inqThreadTypeID) ||
        selectThreadFieldPref(state, 'Item', inqThreadTypeID) ||
        selectThreadFieldPref(state, 'Qty', inqThreadTypeID) ||
        selectThreadFieldPref(state, 'AsgnTo', inqThreadTypeID) ||
        selectThreadFieldPref(state, 'NextTsk', inqThreadTypeID) ||
        selectThreadFieldPref(state, 'Since', inqThreadTypeID) ||
        selectThreadFieldPref(state, 'Source', inqThreadTypeID) ||
        selectThreadFieldPref(state, 'Ref', inqThreadTypeID)
    );

    const refPref = useSelector(state => selectThreadFieldPref(state, 'Ref', inqThreadTypeID));

    const hasConnExtraFields = masterData.extraFields && masterData.extraFields.length > 0;
    const thrExtraFields = useSelector(state => state.masterData.thrExtraFields || []);
    const hasInqExtraFields = thrExtraFields.some(t => t.TID === inqThreadTypeID && t.Fields && JSON.parse(t.Fields).some(f => !f.IsD));

    const isFirstConnection = !connectionData || connectionData.length === 0;
    // Show settings in Add Mode if:
    // 1. Not first time user
    // 2. Logic matches the "Skip Step 2" condition: if fields are hidden, show button to allow enabling them.
    const showAddModeSettings = !activeConnID && !isFirstConnection && (
        isInquiryChecked
            ? (!hasMoreDetailsEnabled && !hasConnExtraFields && !refPref && !hasInqExtraFields)
            : (!hasMoreDetailsEnabled && !hasConnExtraFields)
    );

    // Calculate dynamic dialog width based on grid columns matching the form logic
    const getDialogWidthClass = () => {
        // Special case: Desktop Create Step 1 - Force narrow width
        // Priority check: placed BEFORE hasMoreDetailsEnabled to ensure Step 1 is always compact
        if (!isMobile && !activeConnID && !isSteptwo) {
            return "w-[100vw] min-h-0 max-h-[100vh] max-w-md sm:max-w-[500px]";
        }

        if (hasMoreDetailsEnabled || connID) {
            return "w-[100vw] min-h-0 max-h-[100vh] sm:max-w-[870px]";
        }

        // Calculate visible columns for basic view
        const calledFromArr = ['THR', 'REC', 'TSK', 'CTRC'];
        const hideConnType = calledFromArr.includes(calledFrom);
        const showConnType = (![2, 3].includes(connViewTypeID) && !hideConnType && !isSmartSellerMode());

        let visibleCount = 2; // Standard: Name + Mobile
        if (!isMobile) visibleCount++; // Always count Email on Desktop
        if (masterData?.regions?.length > 0 && connViewTypeID != 5) visibleCount++;

        // Determine grid columns: 4 if ConnType shown, 2 if Edit mode, else calculated
        const gridCols = showConnType ? 4 : (activeConnID ? 2 : Math.max(2, visibleCount));

        // Return specific widths based on column count
        const baseClasses = "w-[100vw] min-h-0 max-h-[100vh]";
        if (gridCols === 2) return `${baseClasses} max-w-md sm:max-w-[550px]`;
        if (gridCols === 3) return `${baseClasses} max-w-lg sm:max-w-[750px]`;

        return connClasses.dialogNoOpenSection; // Default for 4 cols
    };

    // Render dialog header with responsive design
    const renderHeader = () => (
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
            <div className="sm:hidden flex items-center justify-between w-full h-8">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {!forceOpen && (
                        <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                            <ArrowLeft className="h-[24px] w-[24px]" />
                            <span className="sr-only">Back</span>
                        </DialogClose>
                    )}
                    <DialogTitle className="text-lg font-medium">
                        <div className="truncate">
                            {isSteptwo
                                ? "More Details"
                                : (connID
                                    ? getMobileHeaderTitle(`Edit Contact`)
                                    : (forceOpen && threadTypeID !== 2) ? 'Inquiry Entry' : (!connID && (calledFrom === 'FORCE_INQUIRY' || calledFrom === 'THREAD_ENTRY') && isMobile && threadTypeID !== 2) ? 'Inquiry Entry' : 'Contact Entry'
                                )
                            }
                        </div>
                    </DialogTitle>
                </div>
                {/* Right Side */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    {/* CONTACTS_ONLY mode: Show delete button for single contact editing */}
                    {(editMode === 'CONTACTS_ONLY' && selectedContactID && isMobile && selectedContactID !== contactsData[0]?.CID) && (
                        <Button
                            variant="ghost"
                            size="default"
                            className="h-8 w-8 p-0 text-sahi-red hover:bg-sahi-red-light"
                            onClick={() => {
                                setContactToDelete(contactsData.findIndex(c => c.CID === selectedContactID));
                                setDialogStates(prev => ({ ...prev, deleteContactAlert: true }));
                            }}
                        >
                            <Trash2 className="h-5 w-5" />
                        </Button>
                    )}
                    {/* Regular mode: Delete entire connection */}
                    {(activeConnID && canDltConnections && connID && editMode !== 'CONTACTS_ONLY') && (
                        <Button
                            variant="ghost"
                            size="default"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                                if (hasActiveTasks) {
                                    toast.error("This contact has tasks associated with it and cannot be deleted.");
                                    return;
                                }
                                setDialogStates(prev => ({ ...prev, deleteAlert: true }));
                            }}
                        >
                            <Trash2 className="h-5 w-5" />
                        </Button>
                    )}
                </div>
            </div>

            <DialogTitle className="hidden sm:block text-lg md:text-xl">
                {isSteptwo
                    ? `More Details - ${formData.businessName || formData.name || 'Contact'}`
                    : (connID ? `Edit Contact - ${formData.businessName || formData.name || 'Loading...'}` : ((forceOpen || calledFrom === 'FORCE_INQUIRY' || calledFrom === 'THREAD_ENTRY') && threadTypeID !== 2) ? 'Inquiry Entry' : 'Contact Entry')
                }
            </DialogTitle>

            <div className="hidden sm:flex items-center gap-2">
                {isSteptwo && (activeConnID && isMobile) && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className={connClasses.iconButton}
                        onClick={() => setDialogStates(prev => ({ ...prev, fields: true }))}
                    >
                        <Settings className="h-5 w-5 " />
                    </Button>
                )}
                {/* {!isMobile && (activeConnID || showAddModeSettings) && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className={`${connClasses.iconButton} sm:text-text-color sm:hover:bg-gray-200`}
                        onClick={() => setDialogStates(prev => ({ ...prev, fields: true }))}
                    >
                        <Settings className="h-5 w-5 text-text-color" />
                    </Button>
                )} */}
                {/* CONTACTS_ONLY mode: Show delete button for single contact editing */}
                {(editMode === 'CONTACTS_ONLY' && selectedContactID && selectedContactID !== contactsData[0]?.CID) && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className={`${connClasses.iconButton} text-sahi-red hover:bg-sahi-red-light`}
                        onClick={() => {
                            setContactToDelete(contactsData.findIndex(c => c.CID === selectedContactID));
                            setDialogStates(prev => ({ ...prev, deleteContactAlert: true }));
                        }}
                    >
                        <Trash2 className="h-5 w-5" />
                    </Button>
                )}
                {/* Regular mode: Delete entire connection */}
                {(activeConnID && canDltConnections && connID && editMode !== 'CONTACTS_ONLY') && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className={`${connClasses.iconButton} !sm:text-text-color !sm:hover-text-text-color !sm:hover:bg-gray-200`}
                        onClick={() => {
                            if (hasActiveTasks) {
                                toast.error("This contact has tasks associated with it and cannot be deleted.");
                                return;
                            }
                            setDialogStates(prev => ({ ...prev, deleteAlert: true }));
                        }}
                    >
                        <Trash2 className="h-5 w-5" />
                    </Button>
                )}
                {!forceOpen && (
                    <DialogClose className="flex-shrink-0" tabIndex={-1}>
                        <X className="h-5 w-5" />
                        <span className="sr-only">Close</span>
                    </DialogClose>
                )}
            </div>
        </DialogHeader>
    );

    return (
        <>
            {triggerButton && (
                <Button onClick={() => setIsOpen(true)} className={triggerClassName}>
                    <Plus className="h-4 w-4" />
                    {triggerText}
                </Button>
            )}

            <Dialog
                open={isOpen && !dialogStates.fields && !dialogStates.masterDialog}
                onOpenChange={handleOpenChange}
                hidden={isOpen && (dialogStates.fields || dialogStates.masterDialog)}
                preventOutsideClick={true}
                preventEscapeKey={forceOpen}
            >
                <DialogContent className={`flex flex-col ${getDialogWidthClass()}`}>
                    {renderHeader()}

                    <div className={`flex-1 ${connClasses.padding} overflow-y-auto scrollbar-no-arrows`} ref={formRef}>
                        {uiStates.isLoadingConnection ? (
                            <div className={`${connClasses.flexCenter} py-12`}>
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                                    <div className="text-sm text-gray-500">Loading contact data...</div>
                                </div>
                            </div>
                        ) : (
                            <div className={`space-y-4 pb-4 ${uiStates.showMoreDetails ? 'lg:space-y-4 xl:space-y-4' : 'space-y-4'}`}>
                                <ConnectionFormFields
                                    formData={formData}
                                    masterData={masterData}
                                    uiStates={uiStates}
                                    sectionStates={sectionStates}
                                    contactsData={contactsData}
                                    connTypes={connTypes}
                                    connID={activeConnID}
                                    isStepTwo={!connID && activeConnID}
                                    savedThreadID={savedThreadID}
                                    currencySymbol={currencySymbol}
                                    taxIDLabel={taxIDLabel}
                                    onInputChange={handleInputChange}
                                    onSelectChange={handleSelectChange}
                                    onDateChange={handleDateChange}
                                    onTimeChange={handleTimeChange}
                                    onConnectionTypeChange={handleConnectionTypeChange}
                                    onEmailShared={handleEmailSharedCheckbox}
                                    onContactChange={handleContactChange}
                                    onDeleteContact={handleDeleteContact}
                                    contactToDelete={contactToDelete}
                                    setContactToDelete={setContactToDelete}
                                    setDialogStates={setDialogStates}
                                    onContactPickerClick={handleContactPickerClick}
                                    onMoreDetailsToggle={(show) => setUIStates(prev => ({ ...prev, showMoreDetails: show }))}
                                    onSectionToggle={(section, value) => {
                                        setSectionStates(prev => ({ ...prev, [section]: value }));
                                        // Reset data if section is unchecked
                                        if (!value) {
                                            if (section === 'showNextTask') {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    nextTaskDT: getTomorrowDate(),
                                                    nextTaskTime: "23:59",
                                                    nextTaskTyp: "",
                                                    nextTaskNotes: "",
                                                    nextTaskPocUserID: authData?.UserID ? authData.UserID.toString() : ""
                                                }));
                                            } else if (section === 'showLastInteraction') {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    recordDate: getTodayDate(),
                                                    recordTime: getRoundedUpTime(),
                                                    recordType: "",
                                                    recordNotes: "",
                                                    interactionBy: authData?.UserID ? authData.UserID.toString() : ""
                                                }));
                                            }
                                        }
                                    }}
                                    isFieldRequired={isFieldRequired}
                                    isFieldDisabled={isFieldDisabled}
                                    isConnectionTypeSelected={isConnectionTypeSelected}
                                    calledFrom={calledFrom}
                                    setUIStates={setUIStates}
                                    onOpenMasterDialog={(masterType, fieldName, searchText) => {
                                        setUIStates(prev => ({ ...prev, selectedField: fieldName }));
                                        setMasterDialogType(masterType);
                                        setUIStates(prev => ({ ...prev, masterSearchText: searchText || '' }));
                                        setDialogStates(prev => ({ ...prev, masterDialog: true }));
                                    }}
                                    isInquiryChecked={isInquiryChecked}
                                    onToggleInquiry={setIsInquiryChecked}
                                    isMoreDetailsChecked={isMoreDetailsChecked}
                                    onToggleMoreDetails={setIsMoreDetailsChecked}
                                    inquiryData={inquiryData}
                                    onInquiryChange={(field, value) => setInquiryData(prev => ({ ...prev, [field]: value }))}
                                    forceShowEmail={!isMobile}
                                    threadTypeID={threadTypeID}
                                    isBasic={isBasic}
                                    editMode={editMode}
                                    selectedContactID={selectedContactID}
                                    isFirstConnection={isFirstConnection}
                                    showEnterInquiry={showEnterInquiry}
                                />

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3">
                                    {isMobile && (activeConnID || ((activeConnID || showAddModeSettings) && isSteptwo)) && canSeeMoreDetails && editMode !== 'CONTACTS_ONLY' && (
                                        <div className={isMobile ? "flex justify-start" : ""}>
                                            <Button
                                                variant="outline"
                                                onClick={() => setDialogStates(prev => ({ ...prev, fields: true }))}
                                                className={`${connClasses.buttonSecondary} ${isMobile ? 'px-2' : 'flex-1 sm:flex-none sm:min-w-[120px]'}`}
                                            >
                                                <Plus className="h-4 w-4" />
                                                More Details
                                            </Button>
                                        </div>
                                    )}

                                    <Button
                                        onClick={handleSaveConnection}
                                        className={`${connClasses.buttonPrimary} flex-1 sm:flex-none sm:min-w-[140px] min-h-[40px]`}
                                    >
                                        <Check className="h-4 w-4" />
                                        {connID ? 'Update Contact' : isSteptwo ? 'Save Details' : ((!connID && (calledFrom === 'FORCE_INQUIRY' || calledFrom === 'THREAD_ENTRY') && threadTypeID !== 2) || (isFirstConnection && threadTypeID !== 2)) ? 'Save Inquiry' : 'Save Contact'}
                                    </Button>

                                    {/* Mark as Cold/Active Button - Full Width for Mobile */}
                                    {connID && isMobile && editMode !== 'CONTACTS_ONLY' && (() => {
                                        const currentConnection = connectionFromDB || getConnectionByID(connID);
                                        const connTypeVal = (formData.connType || currentConnection?.ConnType || "").toString().toUpperCase();
                                        const isSupplierOrOther = connTypeVal.includes("S") || connTypeVal.includes("O") || connTypeVal.includes("SUPPLIER") || connTypeVal.includes("OTHER");
                                        if (isSupplierOrOther) return null;

                                        const isCold = currentConnection?.IsCold === 1 || currentConnection?.IsCold === true;

                                        const shouldShowButton = !hasOpenInquiries || (hasOpenInquiries && isCold);

                                        if (!shouldShowButton) return null;

                                        return (
                                            <Button
                                                onClick={handleToggleColdContact}
                                                className={`${connClasses.buttonSecondary} w-full`}
                                            >
                                                <GiIceCube className="h-4 w-4" />
                                                {isCold ? 'Mark as Active' : 'Mark as Cold'}
                                            </Button>
                                        );
                                    })()}


                                    {/* More Details Button - Show in Edit Mode or if Settings needed in Add Mode */}
                                    {!isMobile && (activeConnID || ((activeConnID || showAddModeSettings) && isSteptwo)) && canSeeMoreDetails && editMode !== 'CONTACTS_ONLY' && (
                                        <div className={isMobile ? "flex justify-start" : ""}>
                                            <Button
                                                variant="outline"
                                                onClick={() => setDialogStates(prev => ({ ...prev, fields: true }))}
                                                className={`${connClasses.buttonSecondary} ${isMobile ? 'px-2' : 'flex-1 sm:flex-none sm:min-w-[120px]'}`}
                                            >
                                                <Plus className="h-4 w-4" />
                                                More Details
                                            </Button>
                                        </div>
                                    )}
                                    {/* Mark as Cold Button - Show when editing. Hide only if has inquiries AND is not cold */}
                                    {connID && !isMobile && editMode !== 'CONTACTS_ONLY' && (() => {
                                        const currentConnection = connectionFromDB || getConnectionByID(connID);
                                        const connTypeVal = (formData.connType || currentConnection?.ConnType || "").toString().toUpperCase();
                                        const isSupplierOrOther = connTypeVal.includes("S") || connTypeVal.includes("O") || connTypeVal.includes("SUPPLIER") || connTypeVal.includes("OTHER");
                                        if (isSupplierOrOther) return null;

                                        const isCold = currentConnection?.IsCold === 1 || currentConnection?.IsCold === true;

                                        // Show button if:
                                        // 1. No open inquiries (always show)
                                        // 2. Has inquiries BUT contact is already cold (show Mark as Active)
                                        const shouldShowButton = !hasOpenInquiries || (hasOpenInquiries && isCold);

                                        if (!shouldShowButton) return null;

                                        return (
                                            <Button
                                                variant="outline"
                                                onClick={handleToggleColdContact}
                                                className={`${connClasses.buttonSecondary} flex-1 sm:flex-none sm:min-w-[140px]`}
                                            // title={isCold ? "Mark this contact as active" : "Mark this contact as cold"}
                                            >
                                                <GiIceCube className="h-4 w-4" />
                                                {isCold ? 'Mark as Active' : 'Mark as Cold'}
                                            </Button>
                                        );
                                    })()}
                                    {!forceOpen && !isMobile && <Button
                                        variant="outline"
                                        onClick={() => setIsOpen(false)}
                                        className={`${connClasses.buttonSecondary} `}
                                    >
                                        <X className="w-3 h-3" />
                                        Close
                                    </Button>}
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <ConnectionFieldsDialog
                open={dialogStates.fields}
                onOpenChange={(open) => {
                    setDialogStates(prev => ({ ...prev, fields: open }));
                    // Always ensure main dialog is open when fields dialog closes
                    if (!open) {
                        setIsOpen(true);
                    }
                }}
                onSave={(fields, threadFields = []) => {
                    setUIStates(prev => ({ ...prev, selectedFields: fields }));
                    setDialogStates(prev => ({ ...prev, fields: false }));
                    // Simply return to the dialog (setIsOpen(true)) after saving preferences
                    // Do NOT run the "shouldClose" logic here, as that is for "Back" button behavior.
                    // Saving fields implies the user wants to see the updated form.
                    setIsOpen(true);
                }}
                initialSelectedFields={uiStates.selectedFields}
                connID={activeConnID}
                forcedFields={!isMobile ? ['email'] : []}
                isInquiryChecked={isInquiryChecked}
                onAddExtraField={(type = 'connExtraFields', threadTypeID = null, fieldName = "") => {
                    setMasterDialogType(type);

                    // If fieldName is passed (clicked pencil), set it as search text so MastersDialog receives it
                    if (fieldName) {
                        setUIStates(prev => ({ ...prev, masterSearchText: fieldName }));
                    }

                    // Atomically close Fields dialog and open Master dialog
                    setDialogStates(prev => ({
                        ...prev,
                        fields: false,
                        masterDialog: true,
                        openedFromFields: true,
                        threadTypeID: threadTypeID || prev.threadTypeID
                    }));
                }}
                isEditingContact={isEditingContact}
                calledFrom="CONNECTION_ENTRY"
                connType={formData.connType}
            />

            <ContactList
                externalOpen={isContactListOpen}
                onOpenChange={setIsContactListOpen}
                contacts={contactList}
                permissionDenied={contactPermissionDenied}
                onRequestPermission={retryContactPermission}
                onContactClick={(contact) => {
                    populateFormWithContact(contact)
                    setIsContactListOpen(false)
                }}
            />

            {(isDetailOpen && !connID) && (
                <ConnectionDetailsWrapper
                    isOpen={isDetailOpen}
                    onClose={() => {
                        setIsDetailOpen(false);
                        setSavedConnID(null);
                    }}
                    connID={savedConnID}
                />
            )}

            <AlertBox
                open={dialogStates.deleteAlert}
                onOpenChange={(open) => setDialogStates(prev => ({ ...prev, deleteAlert: open }))}
                title="Please Confirm"
                description="Are you sure you wish to delete this Contact?"
                actions={[
                    {
                        label: "Cancel",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto",
                        icon: <X className="h-4 w-4" />
                    },
                    {
                        label: "Delete Contact",
                        variant: "action",
                        value: "delete",
                        className: "w-full sm:w-auto",
                        icon: <Trash2 className="h-4 w-4" />
                    }
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "delete") {
                        handleDeleteConnection();
                    }
                }}
            />

            <AlertBox
                open={dialogStates.coldAlert}
                onOpenChange={(open) => setDialogStates(prev => ({ ...prev, coldAlert: open }))}
                title={(() => {
                    const currentConnection = connectionFromDB || getConnectionByID(connID);
                    const isCold = currentConnection?.IsCold === 1 || currentConnection?.IsCold === true;
                    return isCold ? "Mark as Active" : "Mark as Cold";
                })()}
                description={(() => {
                    const currentConnection = connectionFromDB || getConnectionByID(connID);
                    const isCold = currentConnection?.IsCold === 1 || currentConnection?.IsCold === true;
                    return isCold
                        ? "Are you sure you want to mark this contact as Active?"
                        : "Are you sure you want to mark this contact as Cold?";
                })()}
                actions={[
                    {
                        label: "No",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto",
                        icon: <X className="h-4 w-4" />
                    },
                    {
                        label: "Yes",
                        variant: "action",
                        value: "confirm",
                        className: "w-full sm:w-auto",
                        icon: <Check className="h-4 w-4" />
                    }
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "confirm") {
                        executeToggleColdContact();
                    }
                }}
            />

            {/* Delete Contact Alert */}
            <AlertBox
                open={dialogStates.deleteContactAlert}
                onOpenChange={(open) => {
                    // Only update dialog state, don't trigger delete on close
                    if (!open) {
                        setContactToDelete(null);
                    }
                    setDialogStates(prev => ({ ...prev, deleteContactAlert: open }));
                }}
                title={editMode === 'CONTACTS_ONLY' && selectedContactID ? `Delete ${contactsData[contactToDelete]?.name || 'Contact'}` : "Delete Contact"}
                description={editMode === 'CONTACTS_ONLY' && selectedContactID ? `Are you sure you want to delete ${contactsData[contactToDelete]?.name || 'this contact'}?` : "Are you sure you want to delete this contact?"}
                actions={[
                    {
                        label: "Cancel",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto",
                        icon: <X className="h-4 w-4" />
                    },
                    {
                        label: "Delete Contact",
                        variant: "action",
                        value: "delete",
                        className: "w-full sm:w-auto",
                        icon: <Trash2 className="h-4 w-4" />
                    }
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "delete" && contactToDelete !== null) {
                        handleDeleteContact(contactToDelete);
                        setContactToDelete(null);
                    } else {
                        setContactToDelete(null);
                    }
                }}
            />

            {dialogStates.masterDialog && (
                <MastersDialog
                    open={dialogStates.masterDialog}
                    onClose={() => {
                        // Check if we need to return to the Fields dialog or close completely
                        setDialogStates(prev => ({
                            ...prev,
                            masterDialog: false,
                            fields: prev.openedFromFields || false, // Re-open fields if flag is set
                            openedFromFields: false // Reset flag
                        }));
                        setMasterDialogType(null);
                        setUIStates(prev => ({ ...prev, selectedField: "" }));
                    }}
                    masterType={masterDialogType}
                    directAdd={true}
                    openEntry={true}
                    prefilledName={uiStates.masterSearchText} // Pass the search text here

                    onProductAdded={(itemName) => {
                        // Handle when ANY item is added from master dialog
                        if (uiStates.selectedField) {
                            // Handle Inquiry Product field specifically
                            if (uiStates.selectedField === 'product') {
                                setInquiryData(prev => ({ ...prev, product: itemName }));
                            }
                            // Set the newly added item to the corresponding field (RegionID)
                            else if (uiStates.selectedField == "regionID") {
                                setSelectedRegion(itemName)
                            } else {
                                // Default form data update
                                setFormData(prev => ({
                                    ...prev,
                                    [uiStates.selectedField]: itemName
                                }));
                            }

                            // Show success message
                            // toast.success(`${itemName} added.`);
                        }
                        setUIStates(prev => ({ ...prev, selectedField: "", masterSearchText: '' })); // Reset search text

                        // Close the master dialog and return to previous state if needed
                        setDialogStates(prev => ({
                            ...prev,
                            masterDialog: false,
                            fields: prev.openedFromFields || false, // Re-open fields if flag is set
                            openedFromFields: false // Reset flag
                        }));
                        setMasterDialogType(null);
                        setUIStates(prev => ({ ...prev, selectedField: "" }));
                    }}
                />
            )}
        </>
    );
};

export default ConnectionEntryDialog;